/**
 * Global Notes browse surface — an entity directory of records that have
 * notes activity. Each row = one anchored entity (Customer, Meter, Scheme,
 * Approval, …), NOT one note. The notes badge in the row opens RowNotesModal
 * scoped to that entity so users with the right permission can read, post,
 * archive, or toggle reportable from one place without navigating into the
 * source listing.
 *
 * Sibling of EventTransactionPaginatedTable on the Audit menu group — uses
 * the same primitives (DataTableRenderer, usePaginationState, SelectFilter)
 * so the surface feels native to the rest of the app.
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";

import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";
import CheckboxInputWithLabel from "@components/form/CheckboxInputWithLabel";
import SelectFilter from "@components/form/SelectFilter";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import DataTableRenderer from "@components/shared/DataTable/DataTableRenderer";
import { usePaginationState } from "@components/shared/DataTable/Pagination";
import NoteIndicatorBadge from "@components/notes/NoteIndicatorBadge";
import RowNotesModal from "@components/notes/RowNotesModal";
import { useNoteEntities } from "@hooks/query/useNoteEntities";
import {
  dateInputToEndOfDay,
  dateInputToStartOfDay,
  NoteEntityRow,
} from "@services/notes";
import { DBTables, NOTEABLE_TABLES } from "@utils/constants/dbTables";
import { formatDateTime } from "@utils/formatDateTime";

const GlobalNotesTable: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const [pagination, setPagination] = usePaginationState();
  const [level1ResourceId, setLevel1ResourceId] = useState("");
  const [referenceTable, setReferenceTable] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [hasReportable, setHasReportable] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openEntity, setOpenEntity] = useState<{
    referenceTable: DBTables;
    referenceId: string;
    label?: string;
  } | null>(null);

  const { data, isLoading, isFetching } = useNoteEntities({
    page: pagination.pageIndex + 1,
    pageLimit: pagination.pageSize,
    level1ResourceId: level1ResourceId || undefined,
    referenceTable: (referenceTable?.value as DBTables) || undefined,
    hasReportable: hasReportable || undefined,
    fromDate: fromDate ? dateInputToStartOfDay(fromDate) : undefined,
    toDate: toDate ? dateInputToEndOfDay(toDate) : undefined,
  });

  const rows = data?.entities ?? [];

  const columns = useMemo<ColumnDef<NoteEntityRow>[]>(
    () => [
      {
        accessorKey: "schemeName",
        header: t("notes.global.col_scheme") as string,
        cell: (info: any) => info.getValue() ?? "—",
      },
      {
        id: "on",
        header: t("notes.global.col_context") as string,
        cell: ({ row }: any) => {
          const typeLabel = t(
            `notes.reference_table.${row.original.referenceTable}`,
            { defaultValue: row.original.referenceTable },
          );
          return (
            <span className="text-sm">
              <span className="font-medium text-gray-900">{typeLabel}</span>
              <span className="text-gray-600"> ({row.original.identifier})</span>
            </span>
          );
        },
      },
      {
        id: "notes",
        header: t("notes.global.col_notes") as string,
        cell: ({ row }: any) => (
          <NoteIndicatorBadge
            count={row.original.noteCount}
            onClick={() =>
              setOpenEntity({
                referenceTable: row.original.referenceTable as DBTables,
                referenceId: row.original.referenceId,
                label: row.original.identifier,
              })
            }
          />
        ),
      },
      {
        accessorKey: "lastNoteAt",
        header: t("notes.global.col_last_activity") as string,
        cell: (info: any) => {
          const raw = info.getValue() as string;
          if (!raw) return "—";
          return formatDateTime(new Date(raw));
        },
      },
    ],
    [t],
  );

  return (
    <>
      <section className="flex flex-col flex-1 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start relative z-30">
          <div>
            <Label htmlFor="notes-global-scheme">
              {t("notes.global.filter_scheme")}
            </Label>
            <SelectLevel1Resource
              id="notes-global-scheme"
              value={level1ResourceId}
              onChange={(option: any) => {
                setLevel1ResourceId(option?.value ?? "");
                setPagination({ ...pagination, pageIndex: 0 });
              }}
              isClearable
            />
          </div>

          <div>
            <Label htmlFor="notes-global-reference">
              {t("notes.global.filter_reference_table")}
            </Label>
            <SelectFilter
              id="notes-global-reference"
              value={referenceTable}
              onChange={(option: any) => {
                setReferenceTable(option);
                setPagination({ ...pagination, pageIndex: 0 });
              }}
              options={NOTEABLE_TABLES.map((table) => ({
                value: table,
                label: t(`notes.reference_table.${table}`, {
                  defaultValue: table,
                }),
              }))}
              isClearable
            />
          </div>

          <div>
            <Label htmlFor="notes-global-from">
              {t("notes.global.filter_from")}
            </Label>
            <TextInput
              type="date"
              id="notes-global-from"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value ?? "");
                setPagination({ ...pagination, pageIndex: 0 });
              }}
            />
          </div>

          <div>
            <Label htmlFor="notes-global-to">
              {t("notes.global.filter_to")}
            </Label>
            <TextInput
              type="date"
              id="notes-global-to"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value ?? "");
                setPagination({ ...pagination, pageIndex: 0 });
              }}
            />
          </div>

          <div className="flex items-end gap-4 sm:col-span-2 lg:col-span-4">
            <CheckboxInputWithLabel
              htmlFor="notes-global-reportable"
              checked={hasReportable}
              onChange={(e) => {
                setHasReportable(e.target.checked);
                setPagination({ ...pagination, pageIndex: 0 });
              }}
              label={t("notes.global.filter_has_reportable")}
              inline
            />
          </div>
        </div>

        <DataTableRenderer
          name="global-notes-entities"
          data={rows}
          columns={columns}
          pagination={pagination}
          pageCount={data?.meta?.lastPage ?? -1}
          onPaginationChange={setPagination}
          isLoading={isLoading}
          isFetching={isFetching}
          stickyHeader
        />
      </section>

      {openEntity && (
        <RowNotesModal
          open
          referenceTable={openEntity.referenceTable}
          referenceId={openEntity.referenceId}
          artifactName={openEntity.label}
          onClose={() => setOpenEntity(null)}
        />
      )}
    </>
  );
};

export default GlobalNotesTable;
